import { useSyncExternalStore, type CSSProperties } from "react";
import { Play, Volume1, Volume2, VolumeX } from "lucide-react";
import {
  ALERT_SOUNDS, loadAlertSoundSettings, playAlertSound, saveAlertSoundSettings, subscribeAlertSoundSettings,
  type AlertSoundId, type AlertSoundSettings as Settings,
} from "../lib/watchAlertSound";

// `useSyncExternalStore` compare les instantanés par référence : on ne relit le stockage
// qu'après un changement signalé, sinon chaque rendu produirait un nouvel objet.
let snapshot: Settings | undefined;
const getSnapshot = () => (snapshot ??= loadAlertSoundSettings());
const subscribe = (onChange: () => void) => subscribeAlertSoundSettings(() => { snapshot = undefined; onChange(); });

function useAlertSoundSettings(): [Settings, (next: Partial<Settings>) => void] {
  const settings = useSyncExternalStore(subscribe, getSnapshot);
  return [settings, (next) => saveAlertSoundSettings({ ...settings, ...next })];
}

/** Choix du son d'alerte des surveillances, du volume et de son activation, propre à ce navigateur. */
export function AlertSoundSettings() {
  const [settings, update] = useAlertSoundSettings();
  const percent = Math.round(settings.volume * 100);
  const VolumeIcon = !settings.enabled || percent === 0 ? VolumeX : percent < 50 ? Volume1 : Volume2;

  const choose = (sound: AlertSoundId) => {
    update({ sound, enabled: true });
    void playAlertSound(sound, settings.volume);
  };

  return (
    <div className="sound-settings">
      <label className="switch-row">
        <span>
          <strong>Alerte sonore</strong>
          <small>Joue un son dès qu’un joueur blacklisté est détecté, quelle que soit la page ouverte.</small>
        </span>
        <input type="checkbox" className="switch" role="switch" checked={settings.enabled}
          onChange={(event) => update({ enabled: event.target.checked })} />
      </label>

      <fieldset className="sound-options" disabled={!settings.enabled}>
        <legend>Son</legend>
        {(Object.keys(ALERT_SOUNDS) as AlertSoundId[]).map((id) => (
          <label key={id} className={settings.sound === id ? "sound-option active" : "sound-option"}>
            <input type="radio" name="alert-sound" value={id} checked={settings.sound === id} onChange={() => choose(id)} />
            <span><strong>{ALERT_SOUNDS[id].label}</strong><small>{ALERT_SOUNDS[id].description}</small></span>
            <button type="button" className="icon-button" aria-label={`Écouter ${ALERT_SOUNDS[id].label}`} title="Écouter"
              onClick={(event) => { event.preventDefault(); void playAlertSound(id, settings.volume); }}>
              <Play size={14} />
            </button>
          </label>
        ))}
      </fieldset>

      <div className="volume-row">
        <label htmlFor="alert-volume">Volume</label>
        <div className="volume-row__control">
          <VolumeIcon size={17} aria-hidden="true" />
          <input id="alert-volume" type="range" min={0} max={100} step={5} value={percent} disabled={!settings.enabled}
            style={{ "--fill": `${percent}%` } as CSSProperties}
            onChange={(event) => update({ volume: Number(event.target.value) / 100 })}
            onPointerUp={() => void playAlertSound(settings.sound, settings.volume)}
            onKeyUp={(event) => { if (event.key.startsWith("Arrow") || event.key === "Home" || event.key === "End") void playAlertSound(settings.sound, settings.volume); }} />
          <output htmlFor="alert-volume">{percent} %</output>
        </div>
      </div>
    </div>
  );
}
